// Userland wallet-management API client (/v1/me/*). SAFE surface only:
// metadata + browser-encrypted ciphertext envelopes. No plaintext seed and no
// money movement ever crosses this client.
//
// Full wallet management is gated by WT_USERLAND_WALLETS_ENABLED. A quote-only
// deployment may expose GET /v1/me/wallets for an existing bearer session so
// Preview can bind an identity, while mutation/vault/export routes stay absent.

import type {
  AccountKeyring,
  AccountKeyringV2,
  AccountSecretEnvelope,
  ClientVaultPayload,
  VaultKDF,
  VaultKeySlot,
} from '../lib/clientVault'
import { VAULT_UNRECOVERABLE_WARNING } from '../lib/clientVault'
import { ApiError, apiFetch } from './client'

export type UserlandWallet = {
  id: string
  address: string
  label: string
  source: 'generated' | 'imported' | 'ton_connect_login'
  activation_status: 'inactive' | 'active' | 'deleted'
  wallet_version: string
  is_primary: boolean
  is_selected: boolean
  archived: boolean
  created_at: string
  activated_at: string | null
  /** PUBLIC v5r1 constructor. An undeployed wallet cannot be quoted without it
   *  and its address is this BOC's hash, so it can never be recomputed from the
   *  address alone. This is where a browser with empty storage gets it back —
   *  it is not seed material and unlocks nothing. Null on rows created before
   *  it was recorded. */
  state_init_boc_base64?: string | null
}

export type WalletLimits = {
  active_count: number
  total_count: number
  active_max: number
  total_max: number
}

export type WalletsResponse = {
  wallets: UserlandWallet[]
  limits: WalletLimits
}

export const WALLET_TRACKER_ENABLED =
  import.meta.env.VITE_WT_USERLAND_WALLET_TRACKER_ENABLED === '1'

export type TrackedWalletRule = {
  id: string
  address: string
  label: string
  enabled: boolean
  min_value_usd: number
  created_at: string
  updated_at: string
}

export type WalletTrackerRulesResponse = {
  rules: TrackedWalletRule[]
  channel: `tracker:${string}`
  limit: { count: number; max: number }
}

export type WalletTrackerNotification = {
  id: string
  sequence: number
  tracked_wallet_id?: string
  action: 'buy' | 'sell' | 'transfer'
  direction?: 'in' | 'out'
  counterparty?: string
  wallet_address: string
  wallet_label: string
  token_master: string
  token_ticker: string
  token_amount: string
  value_usd?: number
  tx_hash: string
  source: string
  occurred_at: string
  read_at?: string | null
  archived_at?: string | null
  created_at: string
}

export type CreateTrackedWalletInput = {
  address: string
  label: string
  enabled?: boolean
  min_value_usd?: number
}

export type UpdateTrackedWalletInput = Partial<
  Pick<TrackedWalletRule, 'label' | 'enabled' | 'min_value_usd'>
>

export type UpdateWalletNotificationInput = {
  read?: boolean
  archived?: boolean
}

type WireVaultEnvelope = {
  kind?: 'standalone-v1' | 'account-dek-v1' | 'account-ecies-v1'
  version: string
  cipher_name: string
  nonce_b64: string
  seed_ciphertext_b64: string
  kdf_name?: string
  kdf_salt_b64?: string
  kdf_params?: VaultKDF
  key_slots?: VaultKeySlot[]
}

type WireAccountVault = {
  vault: {
    version: string
    kdf_name: string
    key_slots: VaultKeySlot[]
    public_key_b64?: string
  }
  created_at: string
  login_secret_b64?: string
}

export type AccountVaultFetch = {
  keyring: AnyAccountKeyring
  loginSecret?: Uint8Array
}

// v1 keyring wraps a symmetric DEK (legacy accounts); v2 wraps an ECDH private
// key and publishes the public half, so create/import need no password.
export type AnyAccountKeyring =
  | ({ kind: 'v1' } & AccountKeyring)
  | ({ kind: 'v2' } & AccountKeyringV2)

type ExportResponse = {
  wallet_id: string
  exported_at: string
  vault: WireVaultEnvelope
}

export type StepUpAction = 'export_seed' | 'sign'

export type StepUpChallenge = {
  id: string
  action: StepUpAction
  method: 'vault_password' | 'telegram_approval'
  status: 'approved' | 'pending'
  expires_at: string
}

const SESSION_KEY = 'wils.userland.session'
const SESSION_EVENT = 'wils:userland-session'
const DEV_AUTH_ENABLED = import.meta.env.VITE_WT_USERLAND_DEV_AUTH === '1'

export type AuthSession = {
  authenticated: true
  account_id: string
}

export type MeIdentity = {
  provider: string
  label: string
}

export type MeSnapshot = {
  authenticated: true
  account_id: string
  uid: string
  display_name: string
  given_name?: string
  provider: string
  session_id: string
  session_expires_at: string
  primary_wallet: UserlandWallet | null
  identities?: MeIdentity[]
  has_avatar?: boolean
}

// sessionBinding is the public session id from GET /v1/me — not the secret
// cookie. Vault device slots bind to it. XSS cannot mint API access from it.
let sessionBinding: { accountId: string; sessionId: string } | null = null

function forgetLegacyLocalToken(): void {
  try {
    localStorage.removeItem(SESSION_KEY)
  } catch {
    // storage unavailable
  }
}

function notifySessionChanged(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(SESSION_EVENT))
}

export function onSessionChanged(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined
  window.addEventListener(SESSION_EVENT, listener)
  // The session is the HttpOnly cookie, so a sign-in or sign-out in ANOTHER
  // tab raises no event here. What it does leave behind is a write to the
  // legacy session key, and `storage` only ever fires in the other tabs. Both
  // signals mean the same thing to a consumer: re-read the session.
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === SESSION_KEY) listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(SESSION_EVENT, listener)
    window.removeEventListener('storage', onStorage)
  }
}

export function getSessionToken(): string | null {
  return sessionBinding?.sessionId ?? null
}

export function getSessionAccountId(): string | null {
  return sessionBinding?.accountId ?? null
}

export function clearSessionToken(): void {
  sessionBinding = null
  forgetLegacyLocalToken()
  notifySessionChanged()
}

function rememberSession(snapshot: MeSnapshot): void {
  const next = { accountId: snapshot.account_id, sessionId: snapshot.session_id }
  const changed =
    sessionBinding?.accountId !== next.accountId || sessionBinding?.sessionId !== next.sessionId
  sessionBinding = next
  forgetLegacyLocalToken()
  if (changed) notifySessionChanged()
}

/** Account password floor, mirrored from auth.MinPasswordLength (backend). */
export const MIN_ACCOUNT_PASSWORD_LENGTH = 10

// Creates the account and starts its session in one call — the route answers
// 201 with the same session body as login. Mounted only behind
// WT_USERLAND_EMAIL_REGISTRATION_ENABLED; probe registrationEnabled first, or
// this is a 404 the user cannot act on.
export type RegisterEmailInput = {
  email: string
  password: string
  vault: {
    version: string
    kdf_name: string
    key_slots: VaultKeySlot[]
    public_key_b64: string
    login_secret_b64?: string
  }
  wallet: {
    label: string
    address: string
    // Public v5r1 constructor for the account's first wallet. Without it the
    // very first Buy on a brand-new (still undeployed) wallet cannot be quoted.
    state_init_boc_base64?: string
    vault: WireVaultEnvelope
  }
  // The invite this account was created through (lib/referredBy). Read by the
  // backend only when the request creates the account; an unknown code is ignored.
  referral_code?: string
}

export async function registerEmail(
  emailOrInput: string | RegisterEmailInput,
  password?: string
): Promise<AuthSession> {
  const body =
    typeof emailOrInput === 'string'
      ? { email: emailOrInput, password }
      : {
          email: emailOrInput.email,
          password: emailOrInput.password,
          vault: emailOrInput.vault,
          wallet: emailOrInput.wallet,
          referral_code: emailOrInput.referral_code,
        }
  await apiFetch<{ expires_at: string; provider: string }>('/v1/auth/email/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return fetchAuthSession()
}

export async function loginEmail(email: string, password: string): Promise<AuthSession> {
  await apiFetch<{ expires_at: string; provider: string }>('/v1/auth/email/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  return fetchAuthSession()
}

export type TonConnectProofWire = {
  timestamp: number
  domain: { lengthBytes: number; value: string }
  signature: string
  payload: string
}

export type TonConnectVerifyInput = {
  address: string
  network: string
  proof: TonConnectProofWire
  state_init: string
  vault?: RegisterEmailInput['vault']
  wallet?: RegisterEmailInput['wallet']
  referral_code?: string
}

export async function fetchTonConnectNonce(): Promise<{ payload: string; expires_at: string }> {
  return apiFetch('/v1/auth/tonconnect/nonce', { method: 'POST' })
}

export type SocialProvider = 'google' | 'telegram'

export type SocialCallbackInput = {
  provider: SocialProvider
  code?: string
  state?: string
  ticket?: string
  vault?: RegisterEmailInput['vault']
  wallet?: RegisterEmailInput['wallet']
  referral_code?: string
}

export async function startSocialLogin(provider: SocialProvider): Promise<{ auth_url: string }> {
  return apiFetch(`/v1/auth/${provider}/start`)
}

export async function completeSocialLogin(input: SocialCallbackInput): Promise<AuthSession> {
  const { provider, ...body } = input
  await apiFetch<{ expires_at: string; provider: string }>(`/v1/auth/${provider}/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return fetchAuthSession()
}

export async function verifyTonConnect(input: TonConnectVerifyInput): Promise<AuthSession> {
  await apiFetch<{ expires_at: string; provider: string }>('/v1/auth/tonconnect/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return fetchAuthSession()
}

export async function logoutSession(): Promise<void> {
  try {
    await apiFetch<unknown>('/v1/auth/logout', { method: 'POST' })
  } catch {
    // cookie may already be gone
  }
  clearSessionToken()
}

export async function fetchMe(): Promise<MeSnapshot> {
  const snapshot = await apiFetch<MeSnapshot>('/v1/me', { cache: 'no-store' })
  rememberSession(snapshot)
  return snapshot
}

export async function fetchAuthSession(): Promise<AuthSession> {
  const snapshot = await fetchMe()
  return { authenticated: true, account_id: snapshot.account_id }
}

async function mintDevSession(): Promise<string> {
  const resp = await apiFetch<{ token: string }>('/v1/me/dev-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  return resp.token
}

async function ensureSessionToken(): Promise<string> {
  const token = getSessionToken()
  if (token) return token
  if (DEV_AUTH_ENABLED) return mintDevSession()
  return ''
}

// Cookie is the session. Bearer is only for the env-gated local dev helper.
async function withSession<T>(run: (token: string) => Promise<T>): Promise<T> {
  const token = await ensureSessionToken()
  try {
    return await run(token)
  } catch (err) {
    // Only code "unauthorized" means the SESSION is dead. A step-up 401
    // (step_up_required / stale challenge) and a vault-not-ready 401 ride the
    // same status on a fully valid cookie session — clearing the token for one
    // of those flickered Guest/Profile right after a successful register.
    if (err instanceof ApiError && err.status === 401 && err.code === 'unauthorized') {
      if (getSessionToken() === token) {
        // This call's token is still the stored one — it is the one the
        // backend just rejected. A stale 401 landing after a re-login fails
        // this identity check and must not touch the fresh session.
        clearSessionToken()
        if (DEV_AUTH_ENABLED) return run(await mintDevSession())
      } else if (DEV_AUTH_ENABLED) {
        // Another call already rotated the dev session — retry on it.
        return run(await ensureSessionToken())
      }
    }
    throw err
  }
}

/** An account-scoped request: the session as every /v1/me call carries it. */
export function sessionFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  return withSession((token) =>
    apiFetch<T>(path, {
      ...init,
      headers: authed(token, init.headers as Record<string, string> | undefined),
    })
  )
}

function authed(token: string, extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...extra }
  if (DEV_AUTH_ENABLED && token) headers.Authorization = `Bearer ${token}`
  return headers
}

export function vaultToWire(payload: ClientVaultPayload): WireVaultEnvelope {
  return {
    version: payload.version,
    cipher_name: payload.cipher.name,
    nonce_b64: payload.cipher.nonceB64,
    seed_ciphertext_b64: payload.seedCiphertextB64,
    kdf_name: payload.kdf.name,
    kdf_salt_b64: payload.kdf.saltB64,
    kdf_params: payload.kdf,
    key_slots: payload.keySlots,
  }
}

export function wireToVault(wire: WireVaultEnvelope): ClientVaultPayload {
  if (!wire.kdf_params || !wire.key_slots) {
    throw new Error('standalone vault envelope missing kdf material')
  }
  return {
    version: wire.version as ClientVaultPayload['version'],
    cipher: {
      name: wire.cipher_name as ClientVaultPayload['cipher']['name'],
      nonceB64: wire.nonce_b64,
    },
    kdf: wire.kdf_params,
    seedCiphertextB64: wire.seed_ciphertext_b64,
    keySlots: wire.key_slots,
    warning: VAULT_UNRECOVERABLE_WARNING,
  }
}

// ---- Account vault (one DEK per account) ----

function fromB64Url(value: string): Uint8Array | undefined {
  if (!value) return undefined
  const padded = value.replaceAll('-', '+').replaceAll('_', '/')
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4))
  try {
    const binary = atob(padded + pad)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
    return bytes.byteLength === 32 ? bytes : undefined
  } catch {
    return undefined
  }
}

function toB64Url(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i] ?? 0)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '')
}

export async function fetchAccountVault(): Promise<AccountVaultFetch | null> {
  try {
    const resp = await withSession((token) =>
      apiFetch<WireAccountVault>('/v1/me/vault', { headers: authed(token) })
    )
    const loginSecret = fromB64Url(resp.login_secret_b64 ?? '')
    if (resp.vault.public_key_b64) {
      return {
        keyring: {
          kind: 'v2',
          version: resp.vault.version as AccountKeyringV2['version'],
          kdfName: resp.vault.kdf_name as AccountKeyringV2['kdfName'],
          keySlots: resp.vault.key_slots,
          publicKeyB64: resp.vault.public_key_b64,
        },
        loginSecret,
      }
    }
    return {
      keyring: {
        kind: 'v1',
        version: resp.vault.version as AccountKeyring['version'],
        kdfName: resp.vault.kdf_name as AccountKeyring['kdfName'],
        keySlots: resp.vault.key_slots,
      },
    }
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null
    throw err
  }
}

export async function enrollAccountLoginWrap(input: {
  publicKeyB64: string
  loginSlot: VaultKeySlot
  loginSecret: Uint8Array
}): Promise<void> {
  await withSession((token) =>
    apiFetch<WireAccountVault>('/v1/me/vault/login-wrap', {
      method: 'POST',
      headers: authed(token),
      body: JSON.stringify({
        public_key_b64: input.publicKeyB64,
        login_slot: input.loginSlot,
        login_secret_b64: toB64Url(input.loginSecret),
      }),
    })
  )
}

// New accounts always get a v2 (asymmetric) keyring.
export async function createAccountVault(keyring: AccountKeyringV2): Promise<void> {
  await withSession((token) =>
    apiFetch<WireAccountVault>('/v1/me/vault', {
      method: 'POST',
      headers: authed(token),
      body: JSON.stringify({
        version: keyring.version,
        kdf_name: keyring.kdfName,
        key_slots: keyring.keySlots,
        public_key_b64: keyring.publicKeyB64,
      }),
    })
  )
}

// Password rotation is intentionally narrower than a generic keyring update:
// the server compare-and-swaps only this slot and rejects any public-key,
// recovery-slot or passkey-slot change.
export async function updateAccountVaultPasswordSlot(
  keyring: AccountKeyringV2,
  expectedPasswordSlot: VaultKeySlot
): Promise<void> {
  const passwordSlots = keyring.keySlots.filter((slot) => slot.kind === 'password')
  if (passwordSlots.length !== 1 || !passwordSlots[0]) {
    throw new Error('account vault must contain exactly one password slot')
  }
  await withSession((token) =>
    apiFetch<WireAccountVault>('/v1/me/vault/password-slot', {
      method: 'PATCH',
      headers: authed(token),
      body: JSON.stringify({
        public_key_b64: keyring.publicKeyB64,
        expected_password_slot: expectedPasswordSlot,
        password_slot: passwordSlots[0],
      }),
    })
  )
}

export async function fetchWallets(): Promise<WalletsResponse> {
  return withSession((token) =>
    apiFetch<WalletsResponse>('/v1/me/wallets', { headers: authed(token) })
  )
}

export type WireLimitOrder = {
  id: string
  wallet: string
  pair_addr: string
  base_token: string
  ticker: string
  dex: string
  side: 'buy' | 'sell'
  amount_label: string
  offer_nano: string
  limit_mcap_usd: number
  limit_price_ton: number | null
  slippage_bps: number
  retry_policy?: import('../lib/limitRetry').LimitRetryPolicy
  retry_state?: import('../lib/limitRetry').LimitRetryState
  preset_index?: number
  buy_slippage_bps?: number
  sell_slippage_bps?: number
  status: 'open' | 'filled' | 'canceled' | 'failed'
  fail_reason?: string
  tx_hash?: string
  triggered_mcap_usd?: number | null
  filled_mcap_usd?: number | null
  filled_at?: number | null
  created_at: number
  updated_at: number
}

export async function fetchLimitOrders(): Promise<WireLimitOrder[]> {
  const resp = await withSession((token) =>
    apiFetch<{ orders: WireLimitOrder[] }>('/v1/me/limit-orders', { headers: authed(token) })
  )
  return resp.orders ?? []
}

export type LimitExecutionEvent = {
  sequence: number
  attempt_id: string
  order_id: string
  wallet: string
  ticker: string
  side: 'buy' | 'sell'
  amount_label: string
  stage: 'pending' | 'submitted' | 'uncertain' | 'confirmed' | 'failed'
  tx_hash?: string
  reason?: string
  at: number
}

export function fetchLimitExecutionEvents(cursor: string, signal: AbortSignal) {
  return withSession((token) =>
    apiFetch<{ cursor: string; reset: boolean; events: LimitExecutionEvent[] }>(
      `/v1/me/limit-orders/events?cursor=${encodeURIComponent(cursor)}`,
      { headers: authed(token), signal }
    )
  )
}

export async function putLimitOrder(order: WireLimitOrder): Promise<WireLimitOrder> {
  return withSession((token) =>
    apiFetch<WireLimitOrder>(`/v1/me/limit-orders/${encodeURIComponent(order.id)}`, {
      method: 'PUT',
      headers: authed(token),
      body: JSON.stringify(order),
    })
  )
}

export async function patchLimitOrderRemote(
  id: string,
  patch: { status?: WireLimitOrder['status']; fail_reason?: string; tx_hash?: string }
): Promise<WireLimitOrder> {
  return withSession((token) =>
    apiFetch<WireLimitOrder>(`/v1/me/limit-orders/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: authed(token),
      body: JSON.stringify(patch),
    })
  )
}

export type WireLimitSigner = {
  wallet: string
  public_key_hex: string
}

export async function enrollLimitSigner(input: {
  wallet: string
  signing_seed_hex: string
}): Promise<WireLimitSigner> {
  return withSession((token) =>
    apiFetch<WireLimitSigner>('/v1/me/limit-signer', {
      method: 'POST',
      headers: authed(token),
      body: JSON.stringify(input),
    })
  )
}

export async function fetchLimitSigner(): Promise<WireLimitSigner> {
  return withSession((token) =>
    apiFetch<WireLimitSigner>('/v1/me/limit-signer', { headers: authed(token) })
  )
}

// ---- Foreign-wallet tracker (event-driven, read-only) ----

export async function fetchWalletTracker(): Promise<WalletTrackerRulesResponse> {
  return withSession((token) =>
    apiFetch<WalletTrackerRulesResponse>('/v1/me/tracked-wallets', {
      headers: authed(token),
    })
  )
}

export async function createTrackedWallet(
  input: CreateTrackedWalletInput
): Promise<TrackedWalletRule> {
  const response = await withSession((token) =>
    apiFetch<{ rule: TrackedWalletRule }>('/v1/me/tracked-wallets', {
      method: 'POST',
      headers: authed(token),
      body: JSON.stringify(input),
    })
  )
  return response.rule
}

export async function updateTrackedWallet(
  id: string,
  input: UpdateTrackedWalletInput
): Promise<TrackedWalletRule> {
  const response = await withSession((token) =>
    apiFetch<{ rule: TrackedWalletRule }>(`/v1/me/tracked-wallets/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: authed(token),
      body: JSON.stringify(input),
    })
  )
  return response.rule
}

export async function deleteTrackedWallet(id: string): Promise<void> {
  await withSession((token) =>
    apiFetch<{ ok: boolean }>(`/v1/me/tracked-wallets/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: authed(token),
    })
  )
}

export async function fetchWalletNotificationsPage(
  afterSequence = 0,
  limit = 200
): Promise<{ notifications: WalletTrackerNotification[]; next_seq: number }> {
  return withSession((token) =>
    apiFetch<{ notifications: WalletTrackerNotification[]; next_seq: number }>(
      `/v1/me/notifications?after_seq=${afterSequence}&limit=${limit}`,
      { headers: authed(token) }
    )
  )
}

export async function fetchAllWalletNotifications(
  afterSequence = 0,
  limit = 200
): Promise<WalletTrackerNotification[]> {
  const notifications: WalletTrackerNotification[] = []
  let cursor = afterSequence
  for (;;) {
    const page = await fetchWalletNotificationsPage(cursor, limit)
    notifications.push(...page.notifications)
    if (page.notifications.length < limit) return notifications
    if (page.next_seq <= cursor) {
      throw new Error('wallet tracker notification cursor did not advance')
    }
    cursor = page.next_seq
  }
}

export async function markAllWalletNotificationsRead(): Promise<void> {
  await withSession((token) =>
    apiFetch<{ ok: boolean }>('/v1/me/notifications/read-all', {
      method: 'POST',
      headers: authed(token),
    })
  )
}

export async function updateWalletNotification(
  id: string,
  input: UpdateWalletNotificationInput
): Promise<WalletTrackerNotification> {
  const response = await withSession((token) =>
    apiFetch<{ notification: WalletTrackerNotification }>(
      `/v1/me/notifications/${encodeURIComponent(id)}`,
      { method: 'PATCH', headers: authed(token), body: JSON.stringify(input) }
    )
  )
  return response.notification
}

export type CreateWalletInput = {
  label: string
  address: string
  // Import-only: contract version the seed's funds live on (create = v5r1).
  walletVersion?: string
  // Public v5r1 constructor, derived in the browser at the same moment as the
  // address. Sending it here is what keeps a later cold-wallet quote out of the
  // vault entirely. Omitted for non-v5r1 imports, which have no v5r1 StateInit.
  stateInitBocBase64?: string
  // Seed encrypted with the unlocked ACCOUNT DEK (account-dek-v1 envelope).
  seed: AccountSecretEnvelope
}

export function accountEciesToWire(seed: AccountSecretEnvelope): WireVaultEnvelope {
  return {
    kind: 'account-ecies-v1',
    version: seed.version,
    cipher_name: seed.cipherName,
    nonce_b64: seed.nonceB64,
    seed_ciphertext_b64: seed.ciphertextB64,
  }
}

async function postWallet(path: string, input: CreateWalletInput): Promise<UserlandWallet> {
  const resp = await withSession((token) =>
    apiFetch<{ wallet: UserlandWallet }>(path, {
      method: 'POST',
      headers: authed(token),
      body: JSON.stringify({
        label: input.label,
        address: input.address,
        ...(input.walletVersion ? { wallet_version: input.walletVersion } : {}),
        ...(input.stateInitBocBase64 ? { state_init_boc_base64: input.stateInitBocBase64 } : {}),
        vault: accountEciesToWire(input.seed),
      }),
    })
  )
  return resp.wallet
}

// ---- Live balance preview (public, wallet-address scoped) ----
// Served from wallet_native_balances (the row the observer keeps); returns
// null while the wallet has no readable row yet (404: the observer is told and
// the value arrives on the wallet channel) so previews degrade to an honest
// em-dash.
export type LiveBalancePreview = {
  balance_nano: string
  /**
   * Logical time of the last transaction applied to the account — strictly
   * increasing, and the ONLY safe key for ordering this snapshot against the
   * `wallet:{address}` stream (the row's own lt, the same one a push carries);
   * `as_of` cannot do it (Go's RFC3339Nano drops trailing zeros, so it does
   * not sort as a string).
   */
  last_lt: string
  /** Masterchain block and account status of the row: the rest of the version
   *  (additive; a push carries them too). */
  mc_seqno?: number
  status?: string
  holdings_count: number
  as_of: string
}

export async function fetchLiveBalancePreview(address: string): Promise<LiveBalancePreview | null> {
  try {
    return await apiFetch<LiveBalancePreview>(`/v1/wallets/${encodeURIComponent(address)}/live`)
  } catch {
    return null
  }
}

export async function createWallet(input: CreateWalletInput): Promise<UserlandWallet> {
  return postWallet('/v1/me/wallets/create', input)
}

export async function importWallet(input: CreateWalletInput): Promise<UserlandWallet> {
  return postWallet('/v1/me/wallets/import', input)
}

/**
 * Hand a wallet's PUBLIC constructor back to its row.
 *
 * Wallets created before the server recorded StateInit have no copy at all, so
 * the first browser that can still derive one heals the row and no other
 * browser ever hits the "undeployed wallet cannot be quoted" wall again. The
 * server verifies the BOC hashes to the wallet's own address and never
 * overwrites a value already there. This grants nothing and asks for nothing.
 */
export async function recordWalletStateInit(id: string, stateInitBocBase64: string): Promise<void> {
  await withSession((token) =>
    apiFetch<{ ok: boolean }>(`/v1/me/wallets/${id}/state-init`, {
      method: 'PUT',
      headers: authed(token),
      body: JSON.stringify({ state_init_boc_base64: stateInitBocBase64 }),
    })
  )
}

export async function renameWallet(id: string, label: string): Promise<void> {
  await withSession((token) =>
    apiFetch<{ ok: boolean }>(`/v1/me/wallets/${id}`, {
      method: 'PATCH',
      headers: authed(token),
      body: JSON.stringify({ label }),
    })
  )
}

export async function deleteWallet(id: string): Promise<void> {
  await withSession((token) =>
    apiFetch<{ ok: boolean }>(`/v1/me/wallets/${id}`, {
      method: 'DELETE',
      headers: authed(token),
    })
  )
}

export async function archiveWallet(id: string): Promise<void> {
  await withSession((token) =>
    apiFetch<{ ok: boolean }>(`/v1/me/wallets/${id}/archive`, {
      method: 'POST',
      headers: authed(token),
    })
  )
}

export async function unarchiveWallet(id: string): Promise<void> {
  await withSession((token) =>
    apiFetch<{ ok: boolean }>(`/v1/me/wallets/${id}/unarchive`, {
      method: 'POST',
      headers: authed(token),
    })
  )
}

export async function setPrimaryWallet(id: string): Promise<void> {
  await withSession((token) =>
    apiFetch<{ ok: boolean }>(`/v1/me/wallets/${id}/primary`, {
      method: 'POST',
      headers: authed(token),
    })
  )
}

export async function clearPrimaryWallet(id: string): Promise<void> {
  await withSession((token) =>
    apiFetch<{ ok: boolean }>(`/v1/me/wallets/${id}/primary`, {
      method: 'DELETE',
      headers: authed(token),
    })
  )
}

export async function setWalletSelected(id: string, selected: boolean): Promise<void> {
  await withSession((token) =>
    apiFetch<{ ok: boolean }>(`/v1/me/wallets/${id}/selected`, {
      method: 'PATCH',
      headers: authed(token),
      body: JSON.stringify({ selected }),
    })
  )
}

// ExportedVault: what the reveal flow decrypts locally. account-dek wallets
// decrypt with the unlocked account session; legacy standalone wallets carry
// their own keyring and decrypt with their per-wallet password.
export type ExportedVault =
  | { kind: 'account-ecies-v1'; seed: AccountSecretEnvelope }
  | { kind: 'account-dek-v1'; seed: AccountSecretEnvelope }
  | { kind: 'standalone-v1'; payload: ClientVaultPayload }

export async function createStepUp(action: StepUpAction): Promise<StepUpChallenge> {
  return withSession((token) =>
    apiFetch<StepUpChallenge>('/v1/me/step-up', {
      method: 'POST',
      headers: authed(token),
      body: JSON.stringify({ action }),
    })
  )
}

export async function fetchStepUp(id: string): Promise<StepUpChallenge> {
  return withSession((token) =>
    apiFetch<StepUpChallenge>(`/v1/me/step-up/${encodeURIComponent(id)}`, {
      headers: authed(token),
    })
  )
}

export async function authorizeSensitiveAction(
  action: StepUpAction,
  pollIntervalMs = 1000
): Promise<string> {
  let challenge = await createStepUp(action)
  while (challenge.status === 'pending') {
    const expiresAt = Date.parse(challenge.expires_at)
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      throw new Error('step-up challenge expired')
    }
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
    challenge = await fetchStepUp(challenge.id)
  }
  if (challenge.status !== 'approved') throw new Error('step-up challenge was not approved')
  return challenge.id
}

// Returns the encrypted envelope for a browser-local decrypt (seed reveal /
// backup). The backend records an audit row; plaintext never appears here.
export async function exportWalletVault(id: string, stepUpID?: string): Promise<ExportedVault> {
  const resp = await withSession((token) =>
    apiFetch<ExportResponse>(`/v1/me/wallets/${id}/export`, {
      method: 'POST',
      headers: authed(token),
      body: stepUpID ? JSON.stringify({ step_up_id: stepUpID }) : undefined,
    })
  )
  if (resp.vault.kind === 'account-ecies-v1' || resp.vault.kind === 'account-dek-v1') {
    return {
      kind: resp.vault.kind,
      seed: {
        version: resp.vault.version as AccountSecretEnvelope['version'],
        cipherName: resp.vault.cipher_name as AccountSecretEnvelope['cipherName'],
        nonceB64: resp.vault.nonce_b64,
        ciphertextB64: resp.vault.seed_ciphertext_b64,
      },
    }
  }
  return { kind: 'standalone-v1', payload: wireToVault(resp.vault) }
}

// Returns only the encrypted account-vault envelope for one local signing
// attempt. A live session is the authorization — trading never consumes a
// step-up challenge (seed reveal still does).
export async function fetchWalletSigningMaterial(id: string): Promise<ExportedVault> {
  const resp = await withSession((token) =>
    apiFetch<ExportResponse>(`/v1/me/wallets/${id}/signing-material`, {
      method: 'POST',
      headers: authed(token),
    })
  )
  if (resp.vault.kind !== 'account-ecies-v1' && resp.vault.kind !== 'account-dek-v1') {
    throw new Error('wallet signing material is not account-bound')
  }
  return {
    kind: resp.vault.kind,
    seed: {
      version: resp.vault.version as AccountSecretEnvelope['version'],
      cipherName: resp.vault.cipher_name as AccountSecretEnvelope['cipherName'],
      nonceB64: resp.vault.nonce_b64,
      ciphertextB64: resp.vault.seed_ciphertext_b64,
    },
  }
}
